<template>
  <div class="list-delivery">
    <b-row class="list-delivery__header-container">
      <b-col cols="12" md="6">
        <div class="d-flex gap-2 list-delivery__title-container">
          <h4 class="card-title">{{ title + ` (${items.length})` }}</h4>
          <i v-if="isError" class="icon-fill_alert-circle" />
        </div>
      </b-col>

      <b-col cols="12" md="6" class="list-delivery__collapse-toggle-container">
        <McButton
          v-if="selectedIdLength"
          variant="secondary-red"
          icon="icon-outline_trash"
          :text="$t('global.delete')"
          @click="handleDeleteSelected"
        />
        <ToggleCollapsable
          :id="title.toLowerCase().split(' ').join('-')"
          :value="isVisible"
          @click="isVisible = !isVisible"
        />
        <McButton
          icon="icon-fill_plus"
          :text="$t('global.addField')"
          :disabled="selectedIdLength > 0"
          @click="handleAdd"
        />
      </b-col>
    </b-row>

    <b-collapse v-model="isVisible">
      <template v-for="(item, index) in items">
        <DeliverySection
          :key="item"
          :id="item"
          :index="itemsLength - index"
          :itemsLength="itemsLength"
          :selected="selectedId[item]"
          @click-delete="handleDelete(item)"
          @click-duplicate="handleDuplicate(item)"
        >
          <template #checkbox="{ id }">
            <McCheckbox :id="id" :disabled="items.length == 1" v-model="selectedId[item]" />
          </template>
        </DeliverySection>
      </template>
    </b-collapse>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'

import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'

import ToggleCollapsable from './ToggleCollapsable'
import DeliverySection from './DeliverySection'

export default {
  components: {
    McButton,
    McCheckbox,
    ToggleCollapsable,
    DeliverySection,
  },
  props: {
    isError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVisible: true,

      selectedId: {},
      items: [],
    }
  },
  computed: {
    ...mapState('delivery', [
      'bulkDeliveryIds',
      'bulkItemValues',
      'bulkItemObjects',
      'bulkLoadValues',
      'bulkLoadObjects',
    ]),
    title() {
      return this.$t('delivery.createBulk.deliveryList')
    },
    itemsLength() {
      return this.items.length
    },
    selectedIdLength() {
      const selectedId = Object.keys(this.selectedId)
      return selectedId.filter((item) => this.selectedId[item] == true).length
    },
  },
  created() {
    const initialObject = { key: this.generateDeliveryId(), value: {} }
    this.$store.dispatch('delivery/setBulkItemValues', { ...initialObject })
    this.$store.dispatch('delivery/setBulkDeliveryIds', [initialObject.key])
    this.$store.dispatch('delivery/setBulkIdError', { key: initialObject.key, value: false })

    this.items.push(initialObject.key)
    this.$set(this.selectedId, initialObject.key, false)
  },
  methods: {
    generateDeliveryId() {
      let id = Date.now()

      while (this.items.includes(id)) {
        id += 1
      }

      return id
    },
    async scrollToDelivery(key) {
      await this.$nextTick()

      const target = document.getElementById(`delivery-section-${key}`)
      const listContainer = this.$el

      if (!target || !listContainer) return

      const stickyHeader = document.querySelector('.header-container')
      const stickyHeaderHeight = stickyHeader?.getBoundingClientRect().height || 0
      const stickyHeaderTop = stickyHeader ? parseFloat(window.getComputedStyle(stickyHeader).top) || 0 : 0
      const scrollOffset = stickyHeaderHeight + stickyHeaderTop + 16
      const containerTop = window.pageYOffset + listContainer.getBoundingClientRect().top - scrollOffset

      window.scrollTo({
        top: Math.max(containerTop, 0),
        behavior: 'smooth',
      })
    },
    async handleAdd() {
      this.isVisible = true

      const newObject = { key: this.generateDeliveryId(), value: {} }
      this.items.unshift(newObject.key)
      this.$set(this.selectedId, newObject.key, false)
      this.$store.dispatch('delivery/setBulkItemValues', { ...newObject })
      this.$store.dispatch('delivery/setBulkDeliveryIds', [...this.items])
      this.$store.dispatch('delivery/setBulkIdError', { key: newObject.key, value: false })

      await this.scrollToDelivery(newObject.key)
    },
    async handleDuplicate(key) {
      this.isVisible = true

      const newKey = this.generateDeliveryId()
      const clonedItemValues = cloneDeep(this.bulkItemValues?.[key] || {})
      const clonedItemObjects = cloneDeep(this.bulkItemObjects?.[key] || {})
      const clonedLoadValues = cloneDeep(this.bulkLoadValues?.[key] || [])
      const clonedLoadObjects = cloneDeep(this.bulkLoadObjects?.[key] || [])

      this.items.unshift(newKey)
      this.$set(this.selectedId, newKey, false)

      this.$store.dispatch('delivery/setBulkItemValues', { key: newKey, value: clonedItemValues })
      this.$store.dispatch('delivery/setBulkItemObjects', { key: newKey, value: clonedItemObjects })
      this.$store.dispatch('delivery/setBulkLoadValues', { key: newKey, value: clonedLoadValues })
      this.$store.dispatch('delivery/setBulkLoadObjects', { key: newKey, value: clonedLoadObjects })
      this.$store.dispatch('delivery/setBulkIdError', { key: newKey, value: false })
      this.$store.dispatch('delivery/setBulkDeliveryIds', [...this.items])

      await this.scrollToDelivery(newKey)
    },
    async handleDelete(key) {
      const newItems = this.items.filter((item) => key != item)
      this.$store.dispatch('delivery/deleteBulkItemValues', { key })
      this.$store.dispatch('delivery/deleteBulkItemObjects', { key })
      this.$store.dispatch('delivery/deleteBulkLoadValues', { key })
      this.$store.dispatch('delivery/deleteBulkLoadObjects', { key })
      this.$store.dispatch('delivery/deleteBulkIdError', { key })
      this.$delete(this.selectedId, key)

      this.$set(this, 'items', newItems)
      this.$store.dispatch('delivery/setBulkDeliveryIds', newItems)
    },
    async handleDeleteSelected() {
      const newItems = []
      const newSelectedId = {}

      this.items.forEach((item) => {
        if (this.selectedId[item]) {
          this.$store.dispatch('delivery/deleteBulkItemValues', { key: item })
          this.$store.dispatch('delivery/deleteBulkItemObjects', { key: item })
          this.$store.dispatch('delivery/deleteBulkLoadValues', { key: item })
          this.$store.dispatch('delivery/deleteBulkLoadObjects', { key: item })
          this.$store.dispatch('delivery/deleteBulkIdError', { key: item })

          return
        }

        newItems.push(item)
        newSelectedId[item] = false
      })

      if (newItems.length == 0) {
        const newObject = { key: this.generateDeliveryId(), value: {} }
        newItems.push(newObject.key)
        this.$store.dispatch('delivery/setBulkItemValues', { ...newObject })
        this.$store.dispatch('delivery/setBulkIdError', { key: newObject.key, value: false })
        newSelectedId[newObject.key] = false
      }

      this.$store.dispatch('delivery/setBulkDeliveryIds', newItems)

      this.$set(this, 'items', newItems)
      this.$set(this, 'selectedId', newSelectedId)
    },
    handleUpdateItemValues({ index, key, value }) {
      const formValues = this.itemValues[index]
      formValues[key] = value

      this.$set(this.itemValues, index, formValues)
    },
    handleUpdateItemObjects({ index, key, value }) {
      const fullObjects = this.itemObjects[index]
      fullObjects[key] = value

      this.$set(this.itemObjects, index, fullObjects)
    },
  },
}
</script>

<style lang="scss" scoped>
.list-delivery {
  margin: 24px 0;

  &__header-container {
    margin-bottom: 12px;

    i {
      font-size: 20px;

      &:before {
        color: $red;
      }
    }
  }

  &__title-container {
    display: flex;
    gap: 4px;
  }

  &__collapse-toggle-container {
    display: flex;
    justify-content: end;
    gap: 8px;
  }
}
</style>
